import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FilterBy, CombineBy, properties } from 'Filter/properties'
import { yearRangeToDateRange } from 'Utils/dateUtils'

interface SliderState {
  /* filterQuery is the query that is used to filter the data in the backend */
  filterQuery: any
  /* selectedMin is the selected minimium value in the slider */
  selectedMin: number
  /* selectedMax is the selected maximum value in the slider */
  selectedMax: number
  selections?: string[]
}

type NullFilterType = {
  column: string
  op: FilterBy
  value_macro: string
  value?: any
  meta: {section: string, label: string}
}

type SliderPayload = { min?: number; max?: number; selections?: string[]}

const deriveSelectionsAndRange = (range, previousSelections, meta) => {
  const rangeObject = {min: undefined, max: undefined}
  if (Array.isArray(range)) {
    if((range.includes('Any') || range.includes('any')) && (!previousSelections.includes('Any') && !previousSelections.includes('any'))) {
      return {selections: ['Any']}
    } else {
      const flattenedRange = range.flat(3)
      const numbers = flattenedRange.filter(value => typeof value === 'number')
      const selections = flattenedRange.filter(value => typeof value !== 'number')
      if(numbers.length) {
        rangeObject.min = Math.min(...numbers)
        rangeObject.max = Math.max(...numbers)
        selections.push(meta?.rangeLabel || 'Specific Range')
        return {selections, rangeObject}
      }
      if (selections.includes(null) || selections.includes(meta?.nullLabel)  || selections.includes(meta?.rangeLabel) || selections.length === 0) {
        return {selections: range.filter(value => value !== 'Any' && value !== 'any')}
      } else if (selections.includes('Include Dynamic Age Range')) {
        return {selections: ['Include Dynamic Age Range']}
      }
    }
  }
  return {selections: [typeof range === 'string' ? range : 'Any']}
}

export const buildSliderProperty = (
  column,
  range: Array<any>,
  meta: { section: string; label: string; selectedValue?: string, nullLabel?: string, rangeLabel?: string, nullValue?: any},
  selections: Array<string> = null,
) => {
  const getColumnValue = () => {
    switch (column) {
      case 'birth_date':
        return [-range[1], -range[0]]
      case 'high_school_grad_date':
        return yearRangeToDateRange(range[0], range[1])
      default:
        return range
    }
  }
  const rangeFilter = {
    column,
    op: FilterBy.RANGE,
    value: getColumnValue(),
    value_macro: column === 'birth_date' ? 'ADD_YR_CUR_DATE' : 'NO_OP',
    meta: {section: meta.section, label: meta.label},
  }
  let nullFilter = null
  if(selections && selections.length) {
    if(selections.includes('Any') || selections.includes('any')) return null
    if(selections.includes(meta?.nullLabel || 'Null')) {
      nullFilter = {column, op: meta?.nullValue ? FilterBy.EQ : FilterBy.NULL, meta: rangeFilter.meta, value_macro: 'NO_OP'}
      if(meta?.nullValue) nullFilter.value = meta.nullValue
      if(selections.includes(meta?.rangeLabel || 'Specific Range')) {
        nullFilter = properties([rangeFilter, nullFilter], CombineBy.OR)
      }
    }
    if(selections.includes('Include Dynamic Age Range')) {
      const dynamicAgeRangeFilter = {
        "operation": "AND", 
        "properties": [
          {
            "column": "age_range_as_of_today_max",
            "op": "n_null"
          },
          {
            "column": "age_range_as_of_today_min",
            "op": "n_null"
          },
          {
            "column": "age_range_as_of_today_max",
            "op": FilterBy.GE,
            "value": range[0]
          },
          {
            "column": "age_range_as_of_today_min",
            "op": FilterBy.LE,
            "value": range[1]
          }
        ]
      }
      return {
        "operation": "OR",
        "properties": [
          nullFilter ? nullFilter : rangeFilter,
          dynamicAgeRangeFilter
        ]
      } 
    }
  }
  return nullFilter ? nullFilter : rangeFilter
}

export const sliderSlice = (
  column: string,
  action: string,
  range: any[],
  meta: { section: string; label: string },
  selections: Array<string> = null
) => {
  return createSlice({
    name: column,
    initialState: {
      filterQuery: '',
      selectedMin: range[0],
      selectedMax: range[1],
      selections
    } as SliderState,
    reducers: {
      [action]: (
        state,
        action: PayloadAction<SliderPayload>
      ) => {
        state.selectedMin = action.payload.min
        state.selectedMax = action.payload.max
        state.filterQuery = buildSliderProperty(
          column,
          [action.payload.min, action.payload.max],
          meta,
          state.selections
        )
      },
      [`${action}Selection`]: (
        state,
        action: PayloadAction<SliderPayload>
      ) => {
        const {selections, rangeObject} = deriveSelectionsAndRange(action.payload.selections, state.selections, meta)
        let minAndMax = [state.selectedMin, state.selectedMax]
        const minAndMaxObject = {}
        if(rangeObject) {
          minAndMax = [rangeObject.min, rangeObject.max]
          minAndMaxObject['selectedMin'] = rangeObject.min
          minAndMaxObject['selectedMax'] = rangeObject.max
        }
        return {...state, ...minAndMaxObject, selections, filterQuery: selections && selections.length ? buildSliderProperty(
          column,
          minAndMax,
          meta,
          selections
        ) : null}
      }
    },
  })
}
